import { useState, useMemo, useEffect, useCallback } from "react";
import { useTranslate } from "@/hooks/use-translate/useTranslate";
import { useDate } from "@/hooks/use-date/useDate";
import { Icon } from "@/components/icons/Icon";
import type { InspectionData } from "../../inspection.d";
import { Pill } from "@/components/pill/Pill";
import { cn } from "@/lib/styles.utils";
import ProjectModal from "../ProjectModal";

interface InspectionGeneralDataMobileProps {
  inspectionData: InspectionData;
}

const InspectionGeneralDataMobile = ({
  inspectionData,
}: InspectionGeneralDataMobileProps) => {
  const translate = useTranslate();

  const [showMore, setShowMore] = useState(false);
  const [projectForModal, setProjectForModal] = useState("");
  const [project, setProject] = useState<string | null>(null);

  useEffect(() => {
    if (inspectionData.project) setProject(inspectionData.project);
  }, []);

  const changeShowMore = () => {
    setShowMore((prev) => !prev);
  };

  const dataBoxes = useMemo(() => {
    if (!inspectionData) return [];

    type KeyValue = { property: string; value: any; key: string };

    const keys: (keyof typeof inspectionData)[] = [
      "project",
      "esnIn",
      "esnOut",
      "nsCradle",
      "owner",
      "colour",
      "isCsPmo",
    ];

    const addValue = (key: string, data: any) => {
      if (key === "isCsPmo") return data ? translate("yes") : "No";
      if (!data) return "-";
      return data;
    };

    const dataObjects: KeyValue[] = keys.map((key) => ({
      property: key !== "isCsPmo" ? translate(key) : translate("indicateCsPmo"),
      value: addValue(key, inspectionData[key]),
      key: key,
    }));

    return dataObjects;
  }, [inspectionData, translate]);

  const openCloseProjectModal = useCallback(() => {
    if (projectForModal === "") {
      if (project) {
        setProjectForModal(project);
      } else {
        setProjectForModal("-");
      }
    } else {
      setProjectForModal("");
    }
  }, [projectForModal, project]);

  const getNewProject = useCallback((project: string) => {
    setProject(project);
    setProjectForModal("");
  }, []);

  return (
    <div>
      <div className="w-full grid grid-cols-2 gap-4 items-center">
        <h1 className="font-ib-bold text-3xl">
          {translate("stand")} {inspectionData?.nsStand}
        </h1>
        <div className="flex justify-end">
          <Pill
            icon={"engine"}
            className="bg-ib-gray-lightest"
            iconClassName="size-4"
            label={
              <p className="text-ib-main flex gap-1">
                <span className="text-ib-gray-main mr-0.5">
                  {translate("engine")}:
                </span>
                <span className="text-ib-gray-darkest">
                  {inspectionData?.engineName}
                </span>
              </p>
            }
          />
        </div>

        {!inspectionData?.endDate ? (
          <FinaletInspectionGeneralDataMobile inspectionData={inspectionData} />
        ) : null}
        <div
          className={cn(
            "flex justify-end",
            inspectionData?.endDate ? "w-full col-span-2" : "",
          )}
        >
          <button
            onClick={changeShowMore}
            className="bg-ib-whithe flex gap-x-2 text-ib-main items-center font-[IberiaText] text-ib-red-main hover:text-ib-red-dark p-2"
          >
            {translate(showMore ? "seeLessDetails" : "seeMoreDetails")}
            <Icon
              name={showMore ? "chevronUp" : "chevronDown"}
              className="size-4"
            />
          </button>
        </div>
      </div>
      {showMore ? (
        <div className="w-full pt-4 grid grid-cols-3 ">
          {dataBoxes.map((box) => (
            <div key={box.key} className={`flex flex-col gap-y-0.5 py-4`}>
              <div className="text-ib-main text-ib-gray-main">
                {box.property}
              </div>
              <div className="flex flex-row gap-x-2 items-center text-ib-main text-ib-gray-darkest font-ib-bold">
                {box.property === translate("project") &&
                !inspectionData.endDate ? (
                  <>
                    {project ? project : null}
                    <button
                      className="text-ib-red-main flex flex-row gap-x-2 font-ib-normal"
                      onClick={openCloseProjectModal}
                    >
                      {!project ? translate("add") : null}
                      <Icon
                        name={!project ? "circlePlus" : "pencil"}
                        className="size-[18px]"
                      />
                    </button>
                  </>
                ) : (
                  box.value
                )}
              </div>
            </div>
          ))}
        </div>
      ) : null}
      {projectForModal ? (
        <ProjectModal
          inspectionId={inspectionData.id}
          project={projectForModal}
          closeModal={openCloseProjectModal}
          getNewProject={getNewProject}
        />
      ) : null}
    </div>
  );
};

export default InspectionGeneralDataMobile;

const FinaletInspectionGeneralDataMobile = ({
  inspectionData,
}: {
  inspectionData: InspectionData;
}) => {
  const { formatDate, getDaysSince } = useDate();
  const translate = useTranslate();

  return (
    <div className="flex items-center gap-3">
      <p className="flex gap-1">
        <span className="text-ib-gray-main">{translate("start")}:</span>
        <span className="text-ib-gray-darkest">
          {formatDate(inspectionData?.createdAt)}
        </span>
      </p>
      <span className="text-ib-main text-ib-gray-darkest font-ib-bold whitespace-nowrap">
        {getDaysSince(inspectionData?.createdAt)}
      </span>
    </div>
  );
};
